import type { ConsentState } from './polarisConsent.types';

/**
 * Reads the visitor's cookie consent from Polaris, TrueVault's consent manager, which joy-web loads
 * as `polaris.truevaultcdn.com/static/dns-link.js`. When the visitor's choice cannot be read or
 * trusted, statistics and marketing are denied.
 */

export const CONSENT_DENIED: ConsentState = { statistics: false, marketing: false };

/**
 * What a visitor with no Polaris cookie gets where no Polaris banner can ever ask them, as on
 * joy.pro and the Joy Planner: statistics on, marketing off.
 */
export const CONSENT_WITHOUT_BANNER: ConsentState = { statistics: true, marketing: false };

/**
 * Polaris writes this cookie for every visitor on withjoy.com as URL-encoded JSON, for example
 * `{"implicit":true,"analyticsPermitted":true,"adsPermitted":true,…}`. `implicit: false` means the
 * visitor chose Accept or Decline.
 */
const POLARIS_COOKIE_PREFIX = 'polaris_consent_settings=';

const POLARIS_SCRIPT_SELECTOR = 'script[src^="https://polaris.truevaultcdn.com/"]';

/**
 * How long to wait for joy-web's async Polaris script to load before deciding from the cookie
 * alone. Segment's stub queues every call meanwhile, so waiting delays sending, not recording.
 */
const POLARIS_LOAD_WAIT_MS = 3000;

/**
 * How long to wait for the `load` of a Polaris script whose fetch has already finished: long enough
 * for a cached script to run, short enough that a blocked one still decides fast.
 */
const FINISHED_FETCH_GRACE_MS = 150;

/** How often to re-read a placeholder cookie while waiting for Polaris to rewrite it. */
const PLACEHOLDER_POLL_MS = 25;

/** How long to wait for Polaris to rewrite a placeholder cookie before deciding from it. */
const PLACEHOLDER_WAIT_MS = 250;

type PolarisSettings = Readonly<Record<string, unknown>>;

type GppPingData = Readonly<{ cmpStatus?: string; signalStatus?: string }>;
type GppEventData = Readonly<{ eventName?: string; data?: unknown }>;
type Gpp = (command: string, callback: (data: (GppPingData & GppEventData) | null | undefined) => void) => void;
type GppWindow = Window & { __gpp?: Gpp };

const readPolarisSettings = (): PolarisSettings | 'absent' | 'malformed' => {
  const entry = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(POLARIS_COOKIE_PREFIX));
  if (!entry) return 'absent';

  try {
    const settings: unknown = JSON.parse(decodeURIComponent(entry.substring(POLARIS_COOKIE_PREFIX.length)));
    return settings !== null && typeof settings === 'object' ? (settings as PolarisSettings) : 'malformed';
  } catch {
    return 'malformed';
  }
};

/**
 * Whether the cookie looks like the placeholder Polaris writes on a first visit before it knows the
 * visitor's region: `implicit: true` with nothing permitted. Polaris rewrites it moments later. A
 * pending EEA visitor on withjoy.com's marketing pages has the same cookie, so the wait is bounded.
 */
const isPlaceholder = (settings: ReturnType<typeof readPolarisSettings>): boolean =>
  typeof settings === 'object' &&
  settings.implicit === true &&
  settings.analyticsPermitted !== true &&
  settings.adsPermitted !== true &&
  settings.personalizationPermitted !== true;

/**
 * Statistics is `analyticsPermitted` and marketing is `adsPermitted`, each granted only on the
 * boolean `true`. A malformed cookie denies both; a missing one gives `missingCookie`.
 */
export const getConsentState = (missingCookie: ConsentState = CONSENT_DENIED): ConsentState => {
  const settings = readPolarisSettings();
  if (settings === 'absent') return missingCookie;
  if (settings === 'malformed') return CONSENT_DENIED;
  return {
    statistics: settings.analyticsPermitted === true,
    marketing: settings.adsPermitted === true,
  };
};

/** Each category granted only when both states grant it. */
export const narrowConsent = (a: ConsentState, b: ConsentState): ConsentState => ({
  statistics: a.statistics && b.statistics,
  marketing: a.marketing && b.marketing,
});

/** Whether the Polaris script's fetch has finished, whether or not it succeeded. */
const hasFinishedFetching = (script: HTMLScriptElement): boolean =>
  typeof performance !== 'undefined' && typeof performance.getEntriesByName === 'function' && performance.getEntriesByName(script.src).length > 0;

/**
 * Calls `callback` once with the consent the Polaris cookie records, read once Polaris is ready:
 * when its GPP `ping` answers `cmpStatus: 'loaded'` and `signalStatus: 'ready'`, or on the GPP
 * `signalStatus` event with `data: 'ready'`. The cookie is read one task later and re-read for up to
 * {@link PLACEHOLDER_WAIT_MS} while it still looks like the first-visit placeholder.
 *
 * Polaris loads `async`, so `__gpp` may not exist yet; then this waits for the script's `load` for up
 * to {@link POLARIS_LOAD_WAIT_MS}, or {@link FINISHED_FETCH_GRACE_MS} once its fetch has finished. When Polaris cannot answer (no script on the page, a script that
 * failed, or a wait that ran out) the cookie decides alone: a returning visitor keeps their recorded
 * choice, a placeholder or malformed cookie is denied, and a missing one gives `missingCookie`.
 */
export const onConsentReady = (callback: (consent: ConsentState) => void, missingCookie: ConsentState = CONSENT_DENIED): void => {
  const global = window as GppWindow;
  let decided = false;
  let readScheduled = false;

  const decide = () => {
    if (decided) return;
    decided = true;
    callback(getConsentState(missingCookie));
  };

  const readWhenFinal = (waitedMs: number) => {
    if (waitedMs < PLACEHOLDER_WAIT_MS && isPlaceholder(readPolarisSettings())) {
      global.setTimeout(() => readWhenFinal(waitedMs + PLACEHOLDER_POLL_MS), PLACEHOLDER_POLL_MS);
      return;
    }
    decide();
  };

  const onReady = () => {
    if (decided || readScheduled) return;
    readScheduled = true;
    global.setTimeout(() => readWhenFinal(0), 0);
  };

  const listenToGpp = (gpp: Gpp) => {
    gpp('ping', ping => {
      if (ping?.cmpStatus === 'loaded' && ping.signalStatus === 'ready') onReady();
    });
    gpp('addEventListener', event => {
      if (event?.eventName === 'signalStatus' && event.data === 'ready') onReady();
    });
  };

  const listenOnceLoaded = () => {
    if (global.__gpp) {
      listenToGpp(global.__gpp);
    } else {
      decide();
    }
  };

  if (global.__gpp) {
    listenToGpp(global.__gpp);
    return;
  }

  const script = document.querySelector<HTMLScriptElement>(POLARIS_SCRIPT_SELECTOR);
  if (!script) {
    decide();
    return;
  }

  // A script whose fetch already finished is about to run or has failed (cross-origin timing looks
  // the same either way), so it gets a short grace for its `load` instead of the full wait.
  const scriptDeadline = global.setTimeout(() => settle(), hasFinishedFetching(script) ? FINISHED_FETCH_GRACE_MS : POLARIS_LOAD_WAIT_MS);
  const settle = () => {
    global.clearTimeout(scriptDeadline);
    script.removeEventListener('load', settle);
    script.removeEventListener('error', settle);
    listenOnceLoaded();
  };
  script.addEventListener('load', settle);
  script.addEventListener('error', settle);
};

/**
 * How a consent change made after Segment loaded is applied on the current page.
 *
 * - `reload`: a category the page loaded with is now denied. Segment cannot unload a destination it
 *   has already loaded, so only a reload stops it.
 * - `next-navigation`: categories were only granted. Nothing runs without consent meanwhile, so the
 *   next navigation does a full page load instead of reloading the page under the visitor.
 * - `none`: the categories match what the page loaded with.
 */
export type ConsentChangeAction = 'none' | 'reload' | 'next-navigation';

const CHANGEABLE_CATEGORIES = ['statistics', 'marketing'] as const;

export const consentChangeAction = (loaded: ConsentState, next: ConsentState): ConsentChangeAction => {
  if (CHANGEABLE_CATEGORIES.some(category => loaded[category] && !next[category])) return 'reload';
  if (CHANGEABLE_CATEGORIES.some(category => !loaded[category] && next[category])) return 'next-navigation';
  return 'none';
};

/**
 * Calls `callback` each time Polaris records a new choice. Polaris dispatches `consentChanged` on
 * `window` after it writes the cookie. Register this only after {@link onConsentReady} has reported:
 * Polaris also dispatches `consentChanged` while it starts up.
 */
export const onConsentChange = (callback: () => void): void => {
  window.addEventListener('consentChanged', callback);
};
